import React, { useState, useEffect } from 'react';
import formulaService from '../../../../services/formulaService';
import { masterService } from '../../../../services/masterService';

export const FormulaStudio = ({
  editingFormula = null,
  departments = [],
  onSaved,
  onCancel
}) => {
  // Cascading Selection State
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [investigations, setInvestigations] = useState([]);
  const [loadingInvs, setLoadingInvs] = useState(false);

  const [selectedInvId, setSelectedInvId] = useState('');
  const [invDetail, setInvDetail] = useState(null);
  const [loadingInvDetail, setLoadingInvDetail] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    formulaName: '',
    formulaCode: '',
    description: '',
    departmentId: '',
    investigationId: '',
    targetObsId: '',
    formulaType: 'Arithmetic',
    expression: '',
    outputUnit: '',
    decimalPlaces: 2,
    roundingMode: 'Round',
    minOutputValue: '',
    maxOutputValue: '',
    inputs: []
  });

  // Simulator State
  const [simValues, setSimValues] = useState({});
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  // Validation State
  const [validationResult, setValidationResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Filter departments to ONLY Pathology (CategoryID === 1 or known pathology subspecialties)
  const pathologyDepartments = departments.filter(d => {
    if (d.categoryId === 1) return true;
    const name = (d.name || d.displayName || '').toUpperCase();
    return (
      name.includes('PATH') ||
      name.includes('BIOCHEM') ||
      name.includes('HEMAT') ||
      name.includes('SEROL') ||
      name.includes('HISTO') ||
      name.includes('CYTO') ||
      name.includes('COAG') ||
      name.includes('BLOOD') ||
      name.includes('GENETIC')
    );
  });

  // Step 1: Department Change -> Cascade Reset
  const handleDepartmentChange = async (deptId) => {
    setSelectedDeptId(deptId);
    setSelectedInvId('');
    setInvDetail(null);
    setInvestigations([]);
    setFormData(prev => ({
      ...prev,
      departmentId: deptId,
      investigationId: '',
      targetObsId: '',
      inputs: [],
      expression: '',
      outputUnit: ''
    }));
    setValidationResult(null);
    setSimResult(null);

    if (!deptId) return;

    setLoadingInvs(true);
    try {
      const res = await masterService.getInvestigations({
        subCategoryId: parseInt(deptId, 10),
        isActive: true,
        pageSize: 100
      });
      const items = res?.data?.items || res?.items || res?.data || [];
      setInvestigations(items);
    } catch (err) {
      console.error('Error fetching department investigations:', err);
      setInvestigations([]);
    } finally {
      setLoadingInvs(false);
    }
  };

  // Step 2: Investigation Change -> Cascade Reset & Load Real Mapped Parameters
  const handleInvestigationChange = async (invId) => {
    setSelectedInvId(invId);
    setInvDetail(null);
    setFormData(prev => ({
      ...prev,
      investigationId: invId,
      targetObsId: '',
      inputs: [],
      expression: '',
      outputUnit: ''
    }));
    setValidationResult(null);
    setSimResult(null);

    if (!invId) return;

    setLoadingInvDetail(true);
    try {
      const res = await masterService.getInvestigationById(parseInt(invId, 10));
      const detail = res?.data || res;
      setInvDetail(detail);
    } catch (err) {
      console.error('Error fetching investigation parameters:', err);
      setInvDetail(null);
    } finally {
      setLoadingInvDetail(false);
    }
  };

  // Load Existing Formula into State
  useEffect(() => {
    if (editingFormula) {
      const deptId = editingFormula.departmentId ? String(editingFormula.departmentId) : '';
      const invId = editingFormula.investigationId ? String(editingFormula.investigationId) : '';

      setSelectedDeptId(deptId);
      setSelectedInvId(invId);

      setFormData({
        formulaName: editingFormula.formulaName || '',
        formulaCode: editingFormula.formulaCode || '',
        description: editingFormula.description || '',
        departmentId: deptId,
        investigationId: invId,
        targetObsId: editingFormula.targetObsId ? String(editingFormula.targetObsId) : '',
        formulaType: editingFormula.formulaType || 'Arithmetic',
        expression: editingFormula.currentVersionDetail?.expression || editingFormula.expression || '',
        outputUnit: editingFormula.outputUnit || '',
        decimalPlaces: editingFormula.decimalPlaces ?? 2,
        roundingMode: editingFormula.roundingMode || 'Round',
        minOutputValue: editingFormula.minOutputValue ?? '',
        maxOutputValue: editingFormula.maxOutputValue ?? '',
        inputs: (editingFormula.inputs || []).map(i => ({
          observationId: i.observationId,
          variableName: i.variableName,
          observationName: i.observationName || i.name || '',
          observationCode: i.observationCode || i.labObservationId || '',
          unit: i.unit || '',
          isMandatory: i.isMandatory ?? true,
          defaultValue: i.defaultValue ?? '',
          sortOrder: i.sortOrder ?? 0
        }))
      });

      // Load investigation list if department is set
      if (deptId) {
        setLoadingInvs(true);
        masterService.getInvestigations({ subCategoryId: parseInt(deptId, 10), isActive: true, pageSize: 100 })
          .then(res => {
            const items = res?.data?.items || res?.items || res?.data || [];
            setInvestigations(items);
          })
          .catch(err => console.error(err))
          .finally(() => setLoadingInvs(false));
      }

      // Load investigation parameters for the editing formula
      if (invId) {
        setLoadingInvDetail(true);
        masterService.getInvestigationById(parseInt(invId, 10))
          .then(res => setInvDetail(res?.data || res))
          .catch(err => console.error(err))
          .finally(() => setLoadingInvDetail(false));
      }
    }
  }, [editingFormula]);

  // Observations belonging to the currently selected Investigation
  const availableObservations = invDetail?.observations || [];

  // Currently selected target observation
  const selectedTargetObs = availableObservations.find(
    o => String(o.id || o.observationId || o.labObservationId) === String(formData.targetObsId)
  );

  // Step 3: Target Observation Change
  const handleTargetObsChange = (obsId) => {
    const chosenObs = availableObservations.find(
      o => String(o.id || o.observationId || o.labObservationId) === String(obsId)
    );

    setFormData(prev => ({
      ...prev,
      targetObsId: obsId,
      outputUnit: chosenObs?.unit || prev.outputUnit,
      // Remove target observation from inputs if it was previously added (prevents self-reference)
      inputs: prev.inputs.filter(i => String(i.observationId) !== String(obsId))
    }));
  };

  // Step 4: Add Input Observation (Only from the SAME Investigation)
  const handleAddInputObservation = (obs) => {
    const obsId = obs.id || obs.observationId;
    if (formData.inputs.some(i => String(i.observationId) === String(obsId))) {
      return; // Already added
    }

    const rawName = obs.observationName || obs.name || `Param_${obsId}`;
    const cleanVarName = rawName.replace(/[^A-Za-z0-9_]/g, '_');

    const newInput = {
      observationId: parseInt(obsId, 10),
      variableName: cleanVarName,
      observationName: rawName,
      observationCode: obs.labObservationId || '',
      unit: obs.unit || '',
      isMandatory: true,
      defaultValue: '',
      sortOrder: formData.inputs.length + 1
    };

    setFormData(prev => ({
      ...prev,
      inputs: [...prev.inputs, newInput]
    }));
  };

  // Remove Input Observation
  const handleRemoveInputObservation = (idx) => {
    setFormData(prev => ({
      ...prev,
      inputs: prev.inputs.filter((_, i) => i !== idx)
    }));
  };

  // Insert token into expression
  const insertToken = (token) => {
    setFormData(prev => ({
      ...prev,
      expression: prev.expression ? `${prev.expression} ${token}` : token
    }));
  };

  // Step 5: Live Validation
  const handleValidateFormula = async () => {
    if (!formData.expression.trim()) {
      setValidationResult({
        isValid: false,
        overallHealth: 'Error',
        checks: [{ checkName: 'Expression', passed: false, message: 'Mathematical expression is empty.' }]
      });
      return;
    }

    try {
      const payload = {
        expression: formData.expression,
        inputs: formData.inputs,
        minOutputValue: formData.minOutputValue ? parseFloat(formData.minOutputValue) : null,
        maxOutputValue: formData.maxOutputValue ? parseFloat(formData.maxOutputValue) : null
      };
      const result = await formulaService.validateFormula(payload);
      setValidationResult(result);
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  // Step 6: Live Simulation (Explicitly Sandboxed)
  const handleRunSimulation = async () => {
    if (!formData.expression) return;
    setSimulating(true);
    setSimResult(null);

    try {
      const inputValuesMap = {};
      formData.inputs.forEach(i => {
        const val = simValues[i.variableName];
        inputValuesMap[i.variableName] = val !== undefined && val !== ''
          ? parseFloat(val)
          : (i.defaultValue ? parseFloat(i.defaultValue) : null);
      });

      const payload = {
        formulaId: editingFormula?.formulaId || 0,
        expression: formData.expression,
        decimalPlaces: parseInt(formData.decimalPlaces, 10) || 2,
        roundingMode: formData.roundingMode,
        inputValues: inputValuesMap
      };

      const res = await formulaService.testFormula(payload);
      setSimResult(res);
    } catch (err) {
      setSimResult({
        success: false,
        errorMessage: err.response?.data?.message || err.message
      });
    } finally {
      setSimulating(false);
    }
  };

  // Step 7: Save Formula
  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!formData.formulaName.trim()) {
      setErrorMsg('Formula Name is mandatory.');
      return;
    }
    if (!formData.formulaCode.trim()) {
      setErrorMsg('Formula Code is mandatory.');
      return;
    }
    if (!formData.departmentId) {
      setErrorMsg('Pathology Department must be selected.');
      return;
    }
    if (!formData.investigationId) {
      setErrorMsg('Investigation / Test must be selected.');
      return;
    }
    if (!formData.targetObsId) {
      setErrorMsg('Target Calculated Observation must be selected from the investigation parameters.');
      return;
    }
    if (!formData.expression.trim()) {
      setErrorMsg('Formula Expression cannot be empty.');
      return;
    }
    if (formData.inputs.length === 0) {
      setErrorMsg('At least one input observation from this investigation must be configured.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        departmentId: parseInt(formData.departmentId, 10),
        investigationId: parseInt(formData.investigationId, 10),
        targetObsId: parseInt(formData.targetObsId, 10),
        decimalPlaces: parseInt(formData.decimalPlaces, 10),
        minOutputValue: formData.minOutputValue ? parseFloat(formData.minOutputValue) : null,
        maxOutputValue: formData.maxOutputValue ? parseFloat(formData.maxOutputValue) : null,
        inputs: formData.inputs.map(i => ({
          observationId: i.observationId,
          variableName: i.variableName,
          isMandatory: i.isMandatory ?? true,
          defaultValue: i.defaultValue ? parseFloat(i.defaultValue) : null,
          sortOrder: i.sortOrder || 0
        }))
      };

      if (editingFormula) {
        await formulaService.updateFormula(editingFormula.formulaId, {
          ...payload,
          changeReason: 'Updated via Pathology Formula Studio'
        });
      } else {
        await formulaService.createFormula(payload);
      }

      onSaved();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Error saving formula');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="builder-grid">
      {/* ── Left Column: Real Hierarchy & Visual Builder ── */}
      <div className="builder-card">
        <div className="builder-card-title">
          <span>{editingFormula ? '🧪 Edit Pathology Formula' : '✨ New Pathology Formula'}</span>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#2563eb' }}>
            Hierarchy: Department → Investigation → Parameter
          </span>
        </div>

        {errorMsg && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '10px',
            fontSize: '13.5px'
          }}>
            ⚠️ {errorMsg}
          </div>
        )}

        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
          {/* ── STEP 1: Department Selector (Pathology Only) ── */}
          <div className="form-group-row">
            <div className="form-field">
              <label>1. Pathology Department *</label>
              <select
                value={selectedDeptId}
                onChange={e => handleDepartmentChange(e.target.value)}
                required
              >
                <option value="">-- Select Pathology Department --</option>
                {pathologyDepartments.map(d => (
                  <option key={d.subCategoryId} value={d.subCategoryId}>
                    {d.displayName || d.name}
                  </option>
                ))}
              </select>
              {pathologyDepartments.length === 0 && (
                <span style={{ fontSize: '11px', color: '#b91c1c' }}>
                  No Pathology departments found in Department Master.
                </span>
              )}
            </div>

            {/* ── STEP 2: Investigation Selector ── */}
            <div className="form-field">
              <label>2. Investigation / Test *</label>
              <select
                value={selectedInvId}
                onChange={e => handleInvestigationChange(e.target.value)}
                disabled={!selectedDeptId || loadingInvs}
                required
              >
                <option value="">
                  {loadingInvs
                    ? 'Loading investigations...'
                    : !selectedDeptId
                    ? '-- Select Department First --'
                    : investigations.length === 0
                    ? '-- No Pathology Tests Found --'
                    : '-- Select Pathology Investigation --'}
                </option>
                {investigations.map(inv => (
                  <option key={inv.investigationId || inv.id} value={inv.investigationId || inv.id}>
                    {inv.investigationName || inv.name} ({inv.testCode || 'INV'})
                  </option>
                ))}
              </select>
              {selectedDeptId && !loadingInvs && investigations.length === 0 && (
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  No active investigations mapped to this department.
                </span>
              )}
            </div>
          </div>

          {/* ── STEP 3: Target Observation Selector (From Selected Investigation) ── */}
          <div className="form-field" style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
              <span style={{ fontWeight: 700, color: '#0f172a' }}>
                3. RESULT PARAMETER (Target Observation jisme value save hogi) *
              </span>
              {selectedTargetObs && (
                <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 600, background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                  ✓ Formula output will fill: {selectedTargetObs.observationName || selectedTargetObs.name}
                </span>
              )}
            </label>
            <p style={{ margin: '0 0 8px 0', fontSize: '11.5px', color: '#64748b' }}>
              Ye formula calculate hone ke baad patient test report me <strong>kis parameter ki value banega</strong>, use yahan chunein (e.g. BUN/Creatinine Ratio, eGFR, Globulin, etc.).
            </p>
            <select
              value={formData.targetObsId}
              onChange={e => handleTargetObsChange(e.target.value)}
              disabled={!selectedInvId || loadingInvDetail}
              required
            >
              <option value="">
                {loadingInvDetail
                  ? 'Loading investigation parameters...'
                  : !selectedInvId
                  ? '-- Select Investigation First --'
                  : availableObservations.length === 0
                  ? '-- No observations mapped to this investigation --'
                  : '-- Select Parameter to Calculate --'}
              </option>
              {availableObservations.map(obs => (
                <option key={obs.id || obs.observationId || obs.labObservationId} value={obs.id || obs.observationId}>
                  {obs.observationName || obs.name} [{obs.labObservationId || 'CODE'}] {obs.unit ? `(${obs.unit})` : ''} - {obs.readingFormat || 'Numeric'}
                </option>
              ))}
            </select>
            {selectedInvId && !loadingInvDetail && availableObservations.length === 0 && (
              <span style={{ fontSize: '11px', color: '#b91c1c' }}>
                No observations are mapped to this investigation in the database.
              </span>
            )}
          </div>

          {/* Target Observation Card */}
          {selectedTargetObs && (
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '12px',
              padding: '14px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  TARGET CALCULATED PARAMETER
                </span>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>
                  {selectedTargetObs.observationName || selectedTargetObs.name}
                </div>
                <div style={{ fontSize: '12.5px', color: '#475569', marginTop: '2px' }}>
                  Code: <code>{selectedTargetObs.labObservationId}</code> | Type: {selectedTargetObs.readingFormat || 'Numeric'} | Default Unit: <strong>{selectedTargetObs.unit || 'None'}</strong>
                </div>
              </div>
              <span style={{ background: '#2563eb', color: '#ffffff', padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 600 }}>
                OUTPUT
              </span>
            </div>
          )}

          {/* ── STEP 4: Input Observations Selector (Same Investigation Only) ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
              4. Input Observations (from {invDetail?.name || 'Selected Investigation'}) *
            </label>

            {!selectedInvId ? (
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>
                Select a Pathology Investigation to view and bind its mapped parameters.
              </div>
            ) : availableObservations.length <= 1 ? (
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>
                This investigation does not have enough parameters to configure a formula.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Available Parameters Chips to Add */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Add from investigation:</span>
                  {availableObservations
                    .filter(obs => String(obs.id || obs.observationId) !== String(formData.targetObsId))
                    .map(obs => {
                      const isAdded = formData.inputs.some(i => String(i.observationId) === String(obs.id || obs.observationId));
                      return (
                        <button
                          key={obs.id || obs.observationId || obs.labObservationId}
                          type="button"
                          onClick={() => handleAddInputObservation(obs)}
                          disabled={isAdded}
                          style={{
                            background: isAdded ? '#e2e8f0' : '#ffffff',
                            border: `1px solid ${isAdded ? '#cbd5e1' : '#93c5fd'}`,
                            color: isAdded ? '#94a3b8' : '#1e40af',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: isAdded ? 'default' : 'pointer'
                          }}
                        >
                          {isAdded ? '✓ ' : '+ '}
                          {obs.observationName || obs.name} {obs.unit ? `(${obs.unit})` : ''}
                        </button>
                      );
                    })}
                </div>

                {/* Configured Inputs List */}
                {formData.inputs.map((inp, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 14px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1'
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '13px', color: '#4338ca', fontFamily: 'monospace' }}>
                      ${inp.variableName}
                    </span>
                    <span style={{ fontSize: '12.5px', color: '#0f172a', fontWeight: 500 }}>
                      {inp.observationName}
                    </span>
                    {inp.unit && (
                      <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px' }}>
                        {inp.unit}
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <button
                      type="button"
                      onClick={() => insertToken(inp.variableName)}
                      style={{
                        background: '#e0e7ff',
                        color: '#3730a3',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11.5px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      + Insert in Formula
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveInputObservation(idx)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title="Remove Input"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── STEP 5: Visual Formula Builder ── */}
          <div className="form-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>5. Formula Expression *</label>
              <button
                type="button"
                onClick={handleValidateFormula}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                ✓ Check Syntax & Parameters
              </button>
            </div>

            <div className="expression-editor-box">
              <textarea
                value={formData.expression}
                onChange={e => setFormData({ ...formData, expression: e.target.value })}
                placeholder="e.g. (Hemoglobin / RBC_Count) * 10"
                rows={3}
                required
              />

              {/* Variable Chips */}
              {formData.inputs.length > 0 && (
                <div className="expression-chips-bar">
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Inputs:</span>
                  {formData.inputs.map((inp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="variable-chip"
                      onClick={() => insertToken(inp.variableName)}
                    >
                      {inp.variableName}
                    </button>
                  ))}
                </div>
              )}

              {/* Operators & Functions */}
              <div className="expression-chips-bar">
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Operators:</span>
                {['+', '-', '*', '/', '^', '(', ')'].map(op => (
                  <button
                    key={op}
                    type="button"
                    className="operator-chip"
                    onClick={() => insertToken(op)}
                  >
                    {op}
                  </button>
                ))}
                <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>Functions:</span>
                {['ABS', 'ROUND', 'FLOOR', 'CEIL', 'SQRT'].map(fn => (
                  <button
                    key={fn}
                    type="button"
                    className="operator-chip"
                    onClick={() => insertToken(`${fn}(`)}
                  >
                    {fn}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Formula Metadata */}
          <div className="form-group-row">
            <div className="form-field">
              <label>Formula Name *</label>
              <input
                type="text"
                value={formData.formulaName}
                onChange={e => setFormData({ ...formData, formulaName: e.target.value })}
                placeholder="e.g. CBC MCH Calculation"
                required
              />
            </div>
            <div className="form-field">
              <label>Formula Code *</label>
              <input
                type="text"
                value={formData.formulaCode}
                onChange={e => setFormData({ ...formData, formulaCode: e.target.value.toUpperCase() })}
                placeholder="e.g. CBC_MCH_CALC"
                required
              />
            </div>
          </div>

          {/* Output Unit & Decimal Precision */}
          <div className="form-group-row">
            <div className="form-field">
              <label>Output Unit</label>
              <input
                type="text"
                value={formData.outputUnit}
                onChange={e => setFormData({ ...formData, outputUnit: e.target.value })}
                placeholder="e.g. pg, mg/dL, %"
              />
            </div>
            <div className="form-field">
              <label>Decimal Precision</label>
              <input
                type="number"
                min="0"
                max="6"
                value={formData.decimalPlaces}
                onChange={e => setFormData({ ...formData, decimalPlaces: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-field">
              <label>Rounding Mode</label>
              <select
                value={formData.roundingMode}
                onChange={e => setFormData({ ...formData, roundingMode: e.target.value })}
              >
                <option value="Round">Round (Standard Half-Up)</option>
                <option value="Floor">Floor (Always Round Down)</option>
                <option value="Ceiling">Ceiling (Always Round Up)</option>
                <option value="Truncate">Truncate (Strip Decimals)</option>
              </select>
            </div>
            <div className="form-field">
              <label>Min / Max Clinical Limits (Optional)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={formData.minOutputValue}
                  onChange={e => setFormData({ ...formData, minOutputValue: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={formData.maxOutputValue}
                  onChange={e => setFormData({ ...formData, maxOutputValue: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Validation Feedback */}
          {validationResult && (
            <div style={{
              background: validationResult.isValid ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${validationResult.isValid ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: '10px',
              padding: '14px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: validationResult.isValid ? '#166534' : '#991b1b' }}>
                {validationResult.isValid ? '✓ All Formula Validation Checks Passed' : '⚠️ Validation Issues Detected'}
              </div>
              {validationResult.checks?.map((c, i) => (
                <div key={i} style={{ fontSize: '12px', color: c.passed ? '#15803d' : '#b91c1c' }}>
                  {c.passed ? '●' : '✕'} {c.checkName}: {c.message}
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              className="btn-secondary-dark"
              style={{ color: '#475569', borderColor: '#cbd5e1' }}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary-gradient"
            >
              {saving ? 'Saving...' : editingFormula ? '💾 Update Formula' : '🚀 Save Formula'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Right Column: Live Clinical Simulator (Sandbox Only) ── */}
      <div className="builder-card">
        <div className="builder-card-title">
          <span>⚡ Live Clinical Simulator</span>
          <span style={{ fontSize: '11px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
            Simulation / Test Data Only
          </span>
        </div>

        <div style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          color: '#92400e',
          padding: '10px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          lineHeight: 1.4
        }}>
          <strong>Sandbox Notice:</strong> This is a simulation testing harness. Values entered below are used strictly for mathematical validation and are NOT stored as patient data.
        </div>

        {formData.inputs.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            Bind at least one input observation to run simulation tests.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {formData.inputs.map((inp, idx) => (
              <div key={idx} className="form-field">
                <label style={{ fontSize: '12px' }}>
                  {inp.variableName} ({inp.observationName}) {inp.unit ? `[${inp.unit}]` : ''}
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Enter test value e.g. 14.0"
                  value={simValues[inp.variableName] || ''}
                  onChange={e => setSimValues({ ...simValues, [inp.variableName]: e.target.value })}
                />
              </div>
            ))}

            <button
              type="button"
              onClick={handleRunSimulation}
              disabled={simulating}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '10px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '13.5px',
                cursor: 'pointer'
              }}
            >
              {simulating ? 'Calculating...' : '▶ Run Simulation Trace'}
            </button>

            {simResult && (
              <div className="simulator-result-box">
                {simResult.success ? (
                  <>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      Calculated Simulation Output:
                    </div>
                    <div className="sim-output-val">
                      {simResult.formattedResult} {formData.outputUnit}
                    </div>

                    {simResult.warnings?.length > 0 && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309', padding: '8px 12px', borderRadius: '6px', fontSize: '12px' }}>
                        {simResult.warnings.map((w, i) => (
                          <div key={i}>⚠️ {w}</div>
                        ))}
                      </div>
                    )}

                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase', marginBottom: '6px' }}>
                        Calculation Trace Steps:
                      </div>
                      {simResult.traceSteps?.map((step, idx) => (
                        <div key={idx} className="trace-step">
                          <span>{step.step}</span>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>
                            {step.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#b91c1c', fontSize: '13px', padding: '10px', background: '#fef2f2', borderRadius: '8px' }}>
                    <strong>Simulation Error:</strong>
                    <div>{simResult.errorMessage}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormulaStudio;
